<template>
  <Modal
    v-model="visible"
    title="批量搜索"
    width="800"
    :mask-closable="false"
    @on-cancel="handleCancel"
  >
    <div class="batch-search-content">
      <Tabs v-model="activeTab">
        <TabPane label="文本关键词" name="text">
          <div class="tab-content">
            <p class="tip">关键词以中文逗号、英文逗号或换行分隔，最多支持 {{ maxKeywords }} 个关键词，最大 {{ maxTextLength }} 字符</p>
            <Input
              v-model="textKeywords"
              type="textarea"
              :rows="8"
              :maxlength="maxTextLength + 100"
              placeholder="请输入商品关键词，多个关键词用逗号分隔，例如：阿莫西林,布洛芬,感冒灵"
            />
            <div class="input-stats" v-if="textKeywords.trim()">
              <span :class="{ 'over-limit': isOverKeywordLimit }">
                关键词：{{ textKeywordCount }} / {{ maxKeywords }}
              </span>
              <span :class="{ 'over-limit': isOverTextLimit }">
                字符：{{ textKeywords.length }} / {{ maxTextLength }}
              </span>
            </div>
          </div>
        </TabPane>
        <TabPane label="Excel导入" name="excel">
          <div class="tab-content">
            <p class="tip">支持拖拽Excel文件到下方区域，或点击按钮上传</p>
            <div
              class="upload-area"
              :class="{ 'drag-over': isDragOver }"
              @drop.prevent="handleDrop"
              @dragover.prevent="isDragOver = true"
              @dragleave.prevent="isDragOver = false"
            >
              <Icon type="ios-cloud-upload" size="52" style="color: #3399ff" />
              <p>将Excel文件拖拽到此处，或</p>
              <Button type="primary" @click="triggerFileInput">点击上传</Button>
              <input
                ref="fileInput"
                type="file"
                accept=".xlsx,.xls"
                style="display: none"
                @change="handleFileSelect"
              />
            </div>
            <div v-if="excelFileName" class="file-info">
              <Icon type="ios-document" />
              <span>{{ excelFileName }}</span>
              <Button type="text" size="small" @click="clearExcel">清除</Button>
            </div>
            <div class="download-tip">
              <Button type="text" @click="downloadTemplate">下载Excel模板</Button>
            </div>
          </div>
        </TabPane>
        <TabPane label="直接填写" name="manual">
          <div class="tab-content">
            <div class="manual-list">
              <div class="list-header">
                <span>商品名称</span>
                <span>规格</span>
                <span>生产厂家</span>
                <span>采购数量</span>
                <span>编号</span>
                <span>操作</span>
              </div>
              <div
                v-for="(item, index) in manualList"
                :key="index"
                class="list-item"
              >
                <Input
                  v-model="item.goods_name"
                  placeholder="商品名称"
                  style="width: 180px"
                />
                <Input
                  v-model="item.specification"
                  placeholder="规格"
                  style="width: 140px"
                />
                <Input
                  v-model="item.manufacturer"
                  placeholder="生产厂家"
                  style="width: 160px"
                />
                <InputNumber
                  v-model="item.quantity"
                  :min="1"
                  style="width: 100px"
                />
                <Input
                  v-model="item.number"
                  placeholder="编号"
                  style="width: 100px"
                />
                <Button type="error" size="small" @click="removeManualItem(index)">删除</Button>
              </div>
              <Button type="dashed" long @click="addManualItem">
                <Icon type="ios-add" />
                新增一行
              </Button>
            </div>
          </div>
        </TabPane>
      </Tabs>
    </div>
    <div slot="footer">
      <Button @click="handleCancel">取消</Button>
      <Button type="primary" @click="handleSubmit" :loading="submitting">批量搜索</Button>
    </div>
  </Modal>
</template>

<script>
import * as XLSX from 'xlsx';

export default {
  name: 'BatchSearch',
  data() {
    return {
      visible: false,
      activeTab: 'text',
      textKeywords: '',
      isDragOver: false,
      excelFile: null,
      excelFileName: '',
      manualList: [
        {
          goods_name: '',
          specification: '',
          manufacturer: '',
          quantity: 1,
          number: '',
        },
      ],
      submitting: false,
      maxKeywords: 100, // 最大关键词数量限制
      maxTextLength: 5000, // 文本最大字符数限制
    };
  },
  computed: {
    textKeywordCount() {
      if (!this.textKeywords.trim()) return 0;
      return this.textKeywords
        .split(/[,，\n]/)
        .map(k => k.trim())
        .filter(k => k).length;
    },
    isOverKeywordLimit() {
      return this.textKeywordCount > this.maxKeywords;
    },
    isOverTextLimit() {
      return this.textKeywords.length > this.maxTextLength;
    },
  },
  methods: {
    show() {
      this.visible = true;
      this.reset();
    },
    hide() {
      this.visible = false;
    },
    reset() {
      this.textKeywords = '';
      this.excelFile = null;
      this.excelFileName = '';
      this.manualList = [
        {
          goods_name: '',
          specification: '',
          manufacturer: '',
          quantity: 1,
          number: '',
        },
      ];
      this.activeTab = 'text';
      this.isDragOver = false;
    },
    handleCancel() {
      this.hide();
    },
    handleTabChange(name) {
      this.activeTab = name;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) {
        this.excelFile = file;
        this.excelFileName = file.name;
      }
    },
    handleDrop(e) {
      this.isDragOver = false;
      const file = e.dataTransfer.files[0];
      if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
        this.excelFile = file;
        this.excelFileName = file.name;
      } else {
        this.$Message.error('请上传Excel文件');
      }
    },
    clearExcel() {
      this.excelFile = null;
      this.excelFileName = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    downloadTemplate() {
      // 创建示例Excel数据
      const data = [
        ['商品名称', '规格', '生产厂家', '采购数量', '编号'],
        ['阿莫西林胶囊', '0.25g*24粒', 'XX制药', 10, 'AMXL001'],
        ['布洛芬缓释胶囊', '0.3g*20粒', 'YY药业', 5, 'BLF001'],
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, '采购计划模板.xlsx');
    },
    addManualItem() {
      this.manualList.push({
        goods_name: '',
        specification: '',
        manufacturer: '',
        quantity: 1,
        number: '',
      });
    },
    removeManualItem(index) {
      if (this.manualList.length > 1) {
        this.manualList.splice(index, 1);
      } else {
        this.$Message.warning('至少保留一行');
      }
    },
    async parseExcel() {
      if (!this.excelFile) {
        return null;
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
            
            if (jsonData.length < 2) {
              reject(new Error('Excel文件格式不正确，至少需要表头和数据行'));
              return;
            }
            
            const headers = jsonData[0].map(h => String(h || '').trim());
            const goodsNameIndex = headers.findIndex(h => 
              h.includes('商品名称') || h.includes('商品名') || h.includes('名称')
            );
            const nameCol = goodsNameIndex >= 0 ? goodsNameIndex : 0;
            const specCol = headers.indexOf('规格');
            const manuCol = headers.indexOf('生产厂家');
            const qtyCol = headers.indexOf('采购数量');
            const numberCol = headers.indexOf('编号');
            
            const result = [];
            for (let i = 1; i < jsonData.length; i++) {
              const row = jsonData[i];
              if (!row) continue;
              
              const item = {
                goods_name: String(row[nameCol] || '').trim(),
                specification: String((specCol >= 0 ? row[specCol] : '') || '').trim(),
                manufacturer: String((manuCol >= 0 ? row[manuCol] : '') || '').trim(),
                quantity: parseInt((qtyCol >= 0 ? row[qtyCol] : 1) || 1, 10) || 1,
                number: String((numberCol >= 0 ? row[numberCol] : '') || '').trim(),
              };
              // 仅当“商品名称 + 编号”都为空时才过滤掉
              if (item.goods_name || item.number) {
                result.push(item);
              }
            }
            resolve(result);
          } catch (err) {
            reject(new Error('Excel解析失败：' + err.message));
          }
        };
        reader.onerror = () => reject(new Error('文件读取失败'));
        reader.readAsArrayBuffer(this.excelFile);
      });
    },
    async handleSubmit() {
      // 避免重复点击提交
      if (this.submitting) return;
      try {
        let keywords = [];
        let manualData = null;
        
        if (this.activeTab === 'text') {
          if (!this.textKeywords.trim()) {
            this.$Message.warning('请输入关键词');
            return;
          }
          // 检查字符数限制
          if (this.textKeywords.length > this.maxTextLength) {
            this.$Message.warning(`文本内容超过限制，最大 ${this.maxTextLength} 字符，当前 ${this.textKeywords.length} 字符`);
            return;
          }
          keywords = this.textKeywords
            .split(/[,，\n]/)
            .map(k => k.trim())
            .filter(k => k);
        } else if (this.activeTab === 'excel') {
          if (!this.excelFile) {
            this.$Message.warning('请上传Excel文件');
            return;
          }
          const excelData = await this.parseExcel();
          // 仅当“商品名称 + 编号”都为空时才过滤掉；关键词优先用商品名称，否则用编号
          keywords = excelData.map(item => (item.goods_name || item.number || '').trim()).filter(k => k);
          manualData = excelData;
        } else if (this.activeTab === 'manual') {
          // 仅当“商品名称 + 编号”都为空时才过滤掉
          const validItems = this.manualList.filter(item => {
            const name = String(item.goods_name || '').trim();
            const number = String(item.number || '').trim();
            return !!(name || number);
          });
          if (validItems.length === 0) {
            this.$Message.warning('请至少填写一条商品信息');
            return;
          }
          // 关键词优先用商品名称，否则用编号（保证“只有编号”的行也能提交）
          keywords = validItems.map(item => (String(item.goods_name || '').trim() || String(item.number || '').trim())).filter(k => k);
          manualData = validItems;
        }

        if (keywords.length === 0) {
          this.$Message.warning('没有有效的关键词');
          return;
        }

        // 检查最大数量限制
        if (keywords.length > this.maxKeywords) {
          this.$Message.warning(`关键词数量超过限制，最多支持 ${this.maxKeywords} 个，当前 ${keywords.length} 个`);
          return;
        }

        // 所有校验通过，开始提交：仅防止同一次操作被连点多次，请求结束后允许再次提交（含同一 Excel）
        this.submitting = true;

        // 提交到后端，创建批量搜索任务
        this.$https.post('/PurchasePlan/batchSearchInit', {
          keywords: keywords,
          source: this.activeTab,
          manual_data: manualData,
        }).then((response) => {
          if (response.code === 200) {
            const data = response.data || response;
            const batchId = data.batch_id;
            if (!batchId) {
              this.$Message.error('创建批量搜索任务失败：任务ID为空');
              this.submitting = false;
              return;
            }

            this.$Message.success('批量搜索任务已创建，正在加载结果...');
            this.hide();

            // 为首屏展示保存第一批结果到 sessionStorage（仅当前 batch 使用）
            const initPayload = {
              batch_id: batchId,
              items: data.items || [],
              processed: data.processed || 0,
              total: data.total || keywords.length,
              not_found_keywords: data.not_found_keywords || [],
              skipped_keywords: data.skipped_keywords || [],
            };
            try {
              sessionStorage.setItem('purchasePlanBatchInit_' + batchId, JSON.stringify(initPayload));
            } catch (e) {
              console.warn('保存批量搜索首批结果失败:', e);
            }

            // 跳转到采购计划详情页，由详情页轮询加载结果
            const targetPath = '/personal/PurchasePlanDetail';
            const query = { from_batch: 1, batch_id: batchId, t: Date.now() };
            const currentPath = this.$route.path;

            if (currentPath === targetPath) {
              this.$router.replace({ path: targetPath, query });
            } else {
              this.$router.push({ path: targetPath, query });
            }
            // 跳转后由新页接管，可重置状态以便下次再打开弹窗能正常提交
            this.submitting = false;
          } else {
            this.$Message.error(response.msg || '搜索失败');
            this.submitting = false;
          }
        }).catch((error) => {
          console.error('批量搜索失败:', error);
          this.$Message.error('搜索失败：' + (error.message || '未知错误'));
          this.submitting = false;
        });
      } catch (error) {
        console.error('批量搜索失败:', error);
        this.$Message.error(error.message || '搜索失败');
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.batch-search-content {
  min-height: 400px;
  
  .tab-content {
    padding: 20px 0;
    
    .tip {
      color: #999;
      font-size: 12px;
      margin-bottom: 10px;
    }
    
    .input-stats {
      display: flex;
      justify-content: space-between;
      color: #666;
      font-size: 12px;
      margin-top: 8px;
      
      .over-limit {
        color: #ff4d4f;
        font-weight: 600;
      }
    }
    
    .upload-area {
      border: 2px dashed #d9d9d9;
      border-radius: 4px;
      padding: 40px;
      text-align: center;
      transition: all 0.3s;
      cursor: pointer;
      
      &.drag-over {
        border-color: #3399ff;
        background-color: #f0f8ff;
      }
      
      p {
        margin: 10px 0;
        color: #666;
      }
    }
    
    .file-info {
      margin-top: 15px;
      padding: 10px;
      background: #f5f5f5;
      border-radius: 4px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    
    .download-tip {
      margin-top: 15px;
      text-align: right;
    }
    
    .manual-list {
      .list-header {
        display: flex;
        gap: 10px;
        padding: 10px 0;
        border-bottom: 1px solid #e8e8e8;
        font-weight: 600;
        font-size: 12px;
        
        span {
          flex: 1;
          text-align: center;
        }
      }
      
      .list-item {
        display: flex;
        gap: 10px;
        padding: 10px 0;
        border-bottom: 1px solid #f0f0f0;
        align-items: center;
        
        > * {
          flex: 1;
        }
      }
    }
  }
}
</style>




// WEBPACK FOOTER //
// src/components/popup/BatchSearch.vue